<template>
    <div class="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
        <!-- Header -->
        <div class="max-w-7xl mx-auto mb-6">
            <div
                class="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
            >
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-900">
                            Agent Reconciliation Report
                        </h1>
                        <p class="mt-1 text-sm text-gray-500">
                            Compare agent actual balances against expected
                            balances to identify discrepancies
                        </p>
                    </div>
                    <div class="flex items-center space-x-2">
                        <span
                            v-if="reportData && reportData.summary_date"
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                        >
                            <i class="bx bx-calendar mr-1"></i>
                            {{ reportData.summary_date }}
                        </span>
                        <span
                            class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                        >
                            <i class="bx bx-check-circle mr-1"></i>
                            Pre-computed Data
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Filters -->
        <div class="max-w-7xl mx-auto mb-6">
            <div
                class="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
                <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                        <label
                            class="block text-xs font-medium text-gray-600 mb-1"
                            >Report Date</label
                        >
                        <input
                            type="date"
                            v-model="filters.date"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                        />
                    </div>
                    <div>
                        <label
                            class="block text-xs font-medium text-gray-600 mb-1"
                            >Sort By</label
                        >
                        <select
                            v-model="filters.sort"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="discrepancy">Discrepancy</option>
                            <option value="agent_name">Agent Name</option>
                            <option value="total_sales">Total Sales</option>
                        </select>
                    </div>
                    <div>
                        <label
                            class="block text-xs font-medium text-gray-600 mb-1"
                            >Order</label
                        >
                        <select
                            v-model="filters.order"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="desc">Descending</option>
                            <option value="asc">Ascending</option>
                        </select>
                    </div>
                    <div class="flex items-end">
                        <button
                            @click="fetchReport"
                            :disabled="loading"
                            class="w-full px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <i class="bx bx-search mr-1"></i>
                            <span v-if="!loading">Generate Report</span>
                            <span v-else>Loading...</span>
                        </button>
                    </div>
                    <div class="flex items-end space-x-2">
                        <button
                            @click="refreshData"
                            :disabled="refreshing"
                            class="flex-1 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <i class="bx bx-refresh mr-1"></i>
                            <span v-if="!refreshing">Refresh</span>
                            <span v-else>Refreshing...</span>
                        </button>
                        <button
                            @click="exportToCSV"
                            :disabled="
                                loading ||
                                !reportData ||
                                reportData.data?.length === 0
                            "
                            class="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <i class="bx bx-download mr-1"></i> Export
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Error State -->
        <div v-if="error" class="max-w-7xl mx-auto mb-6">
            <div class="bg-red-50 border border-red-200 rounded-lg p-4">
                <div class="flex items-center">
                    <i class="bx bx-error-circle text-red-600 text-xl mr-2"></i>
                    <p class="text-sm text-red-800">{{ error }}</p>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="max-w-7xl mx-auto">
            <div
                class="bg-white rounded-lg shadow-sm border border-gray-200 p-12"
            >
                <div class="flex flex-col items-center justify-center">
                    <div
                        class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"
                    ></div>
                    <p class="text-gray-600">Loading reconciliation data...</p>
                </div>
            </div>
        </div>

        <!-- Report Content -->
        <div
            v-else-if="!loading && reportData && reportData.data?.length > 0"
            class="max-w-7xl mx-auto"
        >
            <!-- Discrepancy Legend -->
            <div
                class="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 flex items-center justify-between"
            >
                <div class="flex items-center space-x-4 text-xs">
                    <span class="font-medium text-gray-700">Legend:</span>
                    <span class="flex items-center">
                        <span
                            class="w-3 h-3 rounded-full bg-green-500 mr-1"
                        ></span>
                        No discrepancy (&lt; D1)
                    </span>
                    <span class="flex items-center">
                        <span
                            class="w-3 h-3 rounded-full bg-yellow-500 mr-1"
                        ></span>
                        Minor (D1 - D100)
                    </span>
                    <span class="flex items-center">
                        <span
                            class="w-3 h-3 rounded-full bg-red-500 mr-1"
                        ></span>
                        Major (&gt; D100)
                    </span>
                </div>
                <div class="text-xs text-gray-500">
                    Formula: Expected = Deposited - Sales - Commission (25%) |
                    <span class="ml-2">
                        <i class="bx bx-time mr-1"></i>
                        Computed:
                        {{
                            reportData.computed_at
                                | moment('MMM DD, YYYY HH:mm')
                        }}
                    </span>
                </div>
            </div>

            <!-- Agents Table with Sticky Header -->
            <div
                class="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
            >
                <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div class="flex items-center justify-between">
                        <h3 class="text-sm font-semibold text-gray-700">
                            Agent Reconciliation Details
                        </h3>
                        <span class="text-xs text-gray-500"
                            >{{ reportData.data?.length }} agents |
                            {{ reportData.totals?.agents_with_discrepancy }}
                            with discrepancy</span
                        >
                    </div>
                </div>

                <div class="overflow-auto max-h-[65vh]">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50 sticky top-0 z-10">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                                >
                                    Agent
                                </th>
                                <th
                                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                                >
                                    Current Balance
                                </th>
                                <th
                                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                                >
                                    Total Deposited
                                </th>
                                <th
                                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                                >
                                    Total Sales
                                </th>
                                <th
                                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                                >
                                    Commission (25%)
                                </th>
                                <th
                                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                                >
                                    Expected Balance
                                </th>
                                <th
                                    class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                                >
                                    Discrepancy
                                </th>
                                <th
                                    class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50"
                                >
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr
                                v-for="agent in reportData.data"
                                :key="agent.agent_id"
                                class="hover:bg-gray-50 transition-colors"
                                :class="getRowClass(agent.discrepancy)"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-medium text-gray-900"
                                    >
                                        {{ agent.agent_name }}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        ID: {{ agent.agent_id }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-medium text-blue-600"
                                    >
                                        GMD
                                        {{ formatPrice(agent.current_balance) }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-medium text-green-600"
                                    >
                                        GMD
                                        {{ formatPrice(agent.total_deposited) }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-medium text-purple-600"
                                    >
                                        GMD
                                        {{ formatPrice(agent.total_sales) }}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        Fees: GMD
                                        {{ formatPrice(agent.total_fees) }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-medium text-orange-600"
                                    >
                                        GMD
                                        {{ formatPrice(agent.our_commission) }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-medium text-gray-900"
                                    >
                                        GMD
                                        {{
                                            formatPrice(agent.expected_balance)
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-bold"
                                        :class="
                                            getDiscrepancyColor(
                                                agent.discrepancy
                                            )
                                        "
                                    >
                                        {{
                                            agent.discrepancy >= 0 ? '+' : ''
                                        }}GMD
                                        {{ formatPrice(agent.discrepancy) }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-center"
                                >
                                    <span
                                        :class="
                                            getStatusBadgeClass(
                                                agent.discrepancy
                                            )
                                        "
                                        class="px-2 py-1 text-xs font-medium rounded-full"
                                    >
                                        {{ getStatusText(agent.discrepancy) }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                        <!-- Totals Footer -->
                        <tfoot
                            class="bg-gray-100 border-t-2 border-gray-300 sticky bottom-0"
                        >
                            <tr class="font-bold">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div
                                        class="text-sm font-bold text-gray-900"
                                    >
                                        TOTALS
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        {{ reportData.totals?.agents_count }}
                                        agents
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-bold text-blue-600"
                                    >
                                        GMD
                                        {{
                                            formatPrice(
                                                reportData.totals
                                                    ?.total_current_balance
                                            )
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-bold text-green-600"
                                    >
                                        GMD
                                        {{
                                            formatPrice(
                                                reportData.totals
                                                    ?.total_deposited
                                            )
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-bold text-purple-600"
                                    >
                                        GMD
                                        {{
                                            formatPrice(
                                                reportData.totals?.total_sales
                                            )
                                        }}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        Fees: GMD
                                        {{
                                            formatPrice(
                                                reportData.totals?.total_fees
                                            )
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-bold text-orange-600"
                                    >
                                        GMD
                                        {{
                                            formatPrice(
                                                reportData.totals
                                                    ?.total_commission
                                            )
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-bold text-gray-900"
                                    >
                                        -
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-right"
                                >
                                    <div
                                        class="text-sm font-bold"
                                        :class="
                                            reportData.totals
                                                ?.total_discrepancy >= 0
                                                ? 'text-green-600'
                                                : 'text-red-600'
                                        "
                                    >
                                        {{
                                            reportData.totals
                                                ?.total_discrepancy >= 0
                                                ? '+'
                                                : ''
                                        }}GMD
                                        {{
                                            formatPrice(
                                                reportData.totals
                                                    ?.total_discrepancy
                                            )
                                        }}
                                    </div>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-center"
                                >
                                    <span
                                        class="text-xs text-gray-500 font-normal"
                                    >
                                        {{
                                            reportData.totals
                                                ?.agents_with_discrepancy
                                        }}
                                        issues
                                    </span>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div
            v-else-if="
                !loading &&
                (!reportData || reportData.data?.length === 0) &&
                !error
            "
            class="max-w-7xl mx-auto"
        >
            <div
                class="bg-white rounded-lg shadow-sm border border-gray-200 p-12"
            >
                <div class="text-center">
                    <i class="bx bx-file text-6xl text-gray-400 mb-4"></i>
                    <h3 class="text-lg font-medium text-gray-900 mb-2">
                        No Data Available
                    </h3>
                    <p class="text-sm text-gray-500 mb-4">
                        No reconciliation data found for the selected date.
                    </p>
                    <button
                        @click="refreshData"
                        :disabled="refreshing"
                        class="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                        <i class="bx bx-refresh mr-1"></i>
                        Compute Summaries
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'AgentReconciliationReport',

    data() {
        return {
            reportData: null,
            loading: false,
            refreshing: false,
            error: null,
            filters: {
                date: '',
                sort: 'discrepancy',
                order: 'desc',
            },
        };
    },

    created() {
        this.setDefaultDate();
        this.fetchReport();
    },

    methods: {
        setDefaultDate() {
            const today = new Date();
            this.filters.date = today.toISOString().split('T')[0];
        },

        async fetchReport() {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get('/report/generate', {
                    params: {
                        report: 'agent_reconciliation',
                        from: this.filters.date,
                        sort: this.filters.sort,
                        order: this.filters.order,
                    },
                });

                this.reportData = response.data;

                if (this.reportData.message === 'No summaries available yet') {
                    this.error =
                        'No summaries available yet. Click "Refresh" to compute.';
                    this.reportData = null;
                }
            } catch (err) {
                console.error('Error fetching report:', err);

                // Let the global axios interceptor handle 401 (redirect to login)
                if (err.response?.status === 401) {
                    return;
                }

                if (err.response) {
                    switch (err.response.status) {
                        case 404:
                            this.error =
                                'No data found for this date. Try refreshing.';
                            break;
                        case 500:
                            this.error =
                                'Server error. Please try again later.';
                            break;
                        default:
                            this.error = 'An unexpected error occurred.';
                    }
                } else if (err.request) {
                    this.error = 'Network error. Please check your connection.';
                } else {
                    this.error = err.message;
                }
            } finally {
                this.loading = false;
            }
        },

        async refreshData() {
            this.refreshing = true;
            this.error = null;

            try {
                await axios.post('/report/agent-reconciliation/refresh', {
                    date: this.filters.date,
                    sync: true,
                });

                // After refresh, fetch the updated report
                await this.fetchReport();
            } catch (err) {
                console.error('Error refreshing data:', err);
                // Let the global axios interceptor handle 401 (redirect to login)
                if (err.response?.status === 401) {
                    return;
                }
                this.error = 'Failed to refresh data. Please try again.';
            } finally {
                this.refreshing = false;
            }
        },

        getDiscrepancyColor(discrepancy) {
            const absDiscrepancy = Math.abs(discrepancy);
            if (absDiscrepancy < 1) return 'text-green-600';
            if (absDiscrepancy <= 100) return 'text-yellow-600';
            return 'text-red-600';
        },

        getRowClass(discrepancy) {
            const absDiscrepancy = Math.abs(discrepancy);
            if (absDiscrepancy > 100) return 'bg-red-50';
            if (absDiscrepancy > 1) return 'bg-yellow-50';
            return '';
        },

        getStatusBadgeClass(discrepancy) {
            const absDiscrepancy = Math.abs(discrepancy);
            if (absDiscrepancy < 1) return 'bg-green-100 text-green-800';
            if (absDiscrepancy <= 100) return 'bg-yellow-100 text-yellow-800';
            return 'bg-red-100 text-red-800';
        },

        getStatusText(discrepancy) {
            const absDiscrepancy = Math.abs(discrepancy);
            if (absDiscrepancy < 1) return 'OK';
            if (absDiscrepancy <= 100) return 'Minor';
            return 'Review';
        },

        formatPrice(value) {
            if (!value && value !== 0) return '0.00';
            const val = parseFloat(value).toFixed(2);
            return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },

        exportToCSV() {
            if (!this.reportData || !this.reportData.data) return;

            const headers = [
                'Agent ID',
                'Agent Name',
                'Current Balance',
                'Total Deposited',
                'Total Sales',
                'Total Fees',
                'Commission (25%)',
                'Expected Balance',
                'Discrepancy',
                'Status',
            ];

            const rows = this.reportData.data.map(agent => {
                return [
                    agent.agent_id,
                    `"${agent.agent_name}"`,
                    agent.current_balance,
                    agent.total_deposited,
                    agent.total_sales,
                    agent.total_fees,
                    agent.our_commission,
                    agent.expected_balance,
                    agent.discrepancy,
                    this.getStatusText(agent.discrepancy),
                ];
            });

            // Add totals row
            rows.push([
                'TOTALS',
                `"${this.reportData.totals?.agents_count} agents"`,
                this.reportData.totals?.total_current_balance,
                this.reportData.totals?.total_deposited,
                this.reportData.totals?.total_sales,
                this.reportData.totals?.total_fees,
                this.reportData.totals?.total_commission,
                '-',
                this.reportData.totals?.total_discrepancy,
                `${this.reportData.totals?.agents_with_discrepancy} with issues`,
            ]);

            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.join(',')),
            ].join('\n');

            const blob = new Blob([csvContent], {
                type: 'text/csv;charset=utf-8;',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `agent_reconciliation_${this.filters.date}.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
        },
    },
};
</script>
